import { PaginatedDataSheets } from '@lune-fe/dapi-models'
import {
    PaginationKeys,
    truncateDateToLocalDate,
    useErrorSnackbar,
    usePaginationState,
} from '@lune-fe/lune-components-lib'
import { MainLayoutContainerNew, Pagination } from '@lune-fe/lune-ui-lib'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'
import Box from '@mui/material/Box'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Range } from 'react-date-range'

import { PredefinedRange } from 'components/DateRangeFilterNew'
import FiltersBoxNew from 'components/FiltersBoxNew'
import FiltersWithQueryParamsNew from 'components/FiltersWithQueryParamsNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import { getAccountsById, getAllDataSheets } from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { SnackbarMessages } from 'SnackbarMessages'
import ManageMappings from 'views/CalculateEmissions/Imports/ManageMappings'
import { ImportsTable } from 'views/CalculateEmissions/index'
import NewCalculationDropdown from 'views/CalculateEmissions/NewCalculationDropdown'
import { NavigationSource } from 'views/CalculateEmissions/Shared/BackButtonWithFromState'
import ActiveFiltersChips from 'views/CalculateEmissions/Tables/ActiveFiltersChips'

export enum ImportsTypeEnum {
    LOGISTICS = 'logistics',
    FINTECH = 'fintech',
    RFQ = 'rfq',
}

const ImportsTypeLabel: Record<ImportsTypeEnum, string> = {
    [ImportsTypeEnum.LOGISTICS]: 'Logistics',
    [ImportsTypeEnum.FINTECH]: 'Fintech',
    [ImportsTypeEnum.RFQ]: 'RFQ',
}

const Imports = () => {
    useTitle('Imports')
    const isTestMode = useIsTestMode()
    const ITEMS_PER_PAGE = 50
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const { restoreAndClearPaginationState, clearPaginationState } = usePaginationState()

    const [queryParams, setQueryParams] = useState<string>()
    const pageHistoryRef = useRef<(string | undefined)[]>([undefined])
    const [paginatedImports, setPaginatedImports] = useState<PaginatedDataSheets>()

    const [loading, setLoading] = useState(true)
    const [filtersInitialized, setFiltersInitialized] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)

    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<Range | undefined>({
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    })
    const [selectedTypes, setSelectedTypes] = useState<ImportsTypeEnum[]>([])
    const [accountNamesMap, setAccountNamesMap] = useState<Record<string, string>>({})

    const [loadingInitialState, setLoadingInitialState] = useState(true)
    const hasRestoredPaginationRef = useRef(false)
    const restoredPageRef = useRef<number>(1)
    const hasCompletedInitialLoadRef = useRef(false)
    const postInitialRunCountRef = useRef(0)
    const previousFiltersRef = useRef<{
        dateRange: Range | undefined
        types: ImportsTypeEnum[]
        accountIds: string[]
    }>({
        dateRange: { startDate: undefined, endDate: undefined },
        types: [],
        accountIds: [],
    })

    useEffect(() => {
        // Restore pagination state based on the calculation type
        const savedState = restoreAndClearPaginationState(PaginationKeys.IMPORTS)
        if (savedState) {
            setCurrentPage(savedState.page)
            pageHistoryRef.current = savedState.cursorHistory
            hasRestoredPaginationRef.current = true
            restoredPageRef.current = savedState.page
        } else {
            restoredPageRef.current = 1
        }
        setLoadingInitialState(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadImports = useCallback(
        async (
            {
                selectedAccountIds,
                types,
                dateRange,
            }: {
                selectedAccountIds: string[]
                types: ImportsTypeEnum[]
                dateRange: Range | undefined
            },
            pageNumber: number,
        ) => {
            setLoading(true)
            const cursor = pageHistoryRef.current[pageNumber - 1]
            const result = await getAllDataSheets({
                limit: ITEMS_PER_PAGE,
                after: cursor,
                accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
                from: dateRange?.startDate
                    ? truncateDateToLocalDate(dateRange.startDate)
                    : undefined,
                through: dateRange?.endDate
                    ? truncateDateToLocalDate(dateRange.endDate)
                    : undefined,
                type: types.length > 0 ? types : undefined,
            })

            if (result.isOk()) {
                const paginatedResult = result.value
                setPaginatedImports(paginatedResult)
                if (paginatedResult.hasMore && paginatedResult.nextCursor) {
                    const nextCursor = paginatedResult.nextCursor
                    const newHistory = [...pageHistoryRef.current]
                    newHistory[pageNumber] = nextCursor
                    pageHistoryRef.current = newHistory
                }
            } else {
                setPaginatedImports({
                    hasMore: false,
                    nextCursor: null,
                    data: [],
                })
                snackbar(SnackbarMessages.GENERIC_ERROR, { error: result.error })
            }
            setLoading(false)
        },
        [snackbar],
    )

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        loadImports(
            {
                selectedAccountIds,
                types: selectedTypes,
                dateRange,
            },
            page,
        )
    }

    // Fetch account names for display purposes:
    // 1. For selected account filter chips - to show account names instead of IDs in the active filters
    // 2. For the client account column in the table - to display account names for each import row
    useEffect(() => {
        const accountsPerPage = paginatedImports?.data
            ? [...new Set(paginatedImports.data.map((sheet) => sheet.accountId))]
            : []
        const selectedAccounts = selectedAccountIds.length > 0 ? selectedAccountIds : []
        const allAccounts = [...accountsPerPage, ...selectedAccounts]

        if (allAccounts.length > 0) {
            getAccountsById({ accountId: allAccounts }).then((accounts) => {
                const namesMap: Record<string, string> = Object.fromEntries(
                    accounts.map((acc) => [acc.id, acc.name]),
                )
                setAccountNamesMap(namesMap)
            })
        } else {
            setAccountNamesMap({})
        }
    }, [selectedAccountIds, paginatedImports])

    useEffect(
        // eslint-disable-next-line complexity
        () => {
            // Wait for both: component initialization AND filters initialization from URL
            if (!loadingInitialState && filtersInitialized) {
                // Use dedicated ref to track if initial load is complete
                // This prevents filter updates during initialization from being treated as filter changes
                const isInitialLoad = !hasCompletedInitialLoadRef.current

                if (isInitialLoad) {
                    // INITIAL LOAD: Load data with URL filters + restored pagination
                    // Don't check for filter changes during initialization - filters may still be settling
                    const targetPage = restoredPageRef.current

                    // Ensure currentPage state is in sync
                    if (currentPage !== targetPage) {
                        setCurrentPage(targetPage)
                    }

                    // Save the initial filter state
                    previousFiltersRef.current = {
                        dateRange,
                        types: selectedTypes,
                        accountIds: selectedAccountIds,
                    }

                    // Mark initial load as complete immediately to prevent subsequent runs from loading again
                    hasCompletedInitialLoadRef.current = true

                    // Load data with restored page
                    loadImports(
                        {
                            selectedAccountIds,
                            types: selectedTypes,
                            dateRange,
                        },
                        targetPage,
                    )
                } else {
                    // POST-INITIAL LOAD: Only reload if filters actually changed
                    // Increment post-initial run counter
                    postInitialRunCountRef.current += 1

                    const filtersChanged =
                        previousFiltersRef.current.dateRange?.startDate !== dateRange?.startDate ||
                        previousFiltersRef.current.dateRange?.endDate !== dateRange?.endDate ||
                        JSON.stringify(previousFiltersRef.current.accountIds) !==
                            JSON.stringify(selectedAccountIds) ||
                        JSON.stringify(previousFiltersRef.current.types) !==
                            JSON.stringify(selectedTypes)

                    // Settling period: only applies when we restored pagination (came back from a detail page)
                    // This accounts for URL filter sync that may happen after filtersInitialized becomes true
                    // If no pagination was restored (first visit), don't use settling period
                    const isSettlingPeriod =
                        hasRestoredPaginationRef.current && postInitialRunCountRef.current <= 1

                    if (filtersChanged) {
                        if (isSettlingPeriod) {
                            // During settling period: update filter state but don't reset pagination or reload
                            previousFiltersRef.current = {
                                dateRange,
                                types: selectedTypes,
                                accountIds: selectedAccountIds,
                            }
                        } else {
                            // Real filter change after settling: reset pagination to page 1
                            clearPaginationState(PaginationKeys.IMPORTS)
                            setCurrentPage(1)
                            pageHistoryRef.current = [undefined]

                            // Update the ref to track current filter state
                            previousFiltersRef.current = {
                                dateRange,
                                types: selectedTypes,
                                accountIds: selectedAccountIds,
                            }

                            // Load data from page 1
                            loadImports(
                                {
                                    selectedAccountIds,
                                    types: selectedTypes,
                                    dateRange,
                                },
                                1,
                            )
                        }
                    }
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [selectedAccountIds, loadingInitialState, dateRange, selectedTypes, filtersInitialized],
    )

    return (
        <MainLayoutContainerNew
            isTestMode={!!isTestMode}
            title={'Imports'}
            subtitle={
                'Browse and filter emissions calculations generated from your spreadsheet uploads'
            }
            primaryButton={
                <>
                    <ManageMappings />
                    <NewCalculationDropdown
                        from={NavigationSource.IMPORTS}
                        onUploadSuccess={(isSuccess) => {
                            if (isSuccess) {
                                clearPaginationState(PaginationKeys.IMPORTS)
                                setCurrentPage(1)
                                pageHistoryRef.current = [undefined]
                                loadImports(
                                    {
                                        selectedAccountIds,
                                        types: selectedTypes,
                                        dateRange,
                                    },
                                    1,
                                )
                            }
                        }}
                    />
                </>
            }
            sx={{
                height: isTestMode ? `calc(100vh - 24px)` : '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '.main-layout-container__header': {
                    top: 0,
                },
                '.main-layout-container__body': {
                    overflowY: 'hidden',
                },
            }}
        >
            <WhiteBoxContainer
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <FiltersWithQueryParamsNew<string>
                    filters={[
                        {
                            queryParamKey: 'account_id',
                            selectedValues: selectedAccountIds,
                            setSelectedValues: (values) => setSelectedAccountIds(values),
                        },
                        {
                            queryParamKey: 'type',
                            selectedValues: selectedTypes,
                            setSelectedValues: (values) =>
                                setSelectedTypes(values as ImportsTypeEnum[]),
                        },
                    ]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    setQueryParams={setQueryParams}
                    onFiltersInitialized={() => setFiltersInitialized(true)}
                />
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <FiltersBoxNew<ImportsTypeEnum>
                        preselectedDateRange={dateRange}
                        setDateRange={setDateRange}
                        preselectedAccountIds={selectedAccountIds}
                        setSelectedAccountIds={setSelectedAccountIds}
                        defaultDateRange={PredefinedRange.THIRTY_DAYS}
                        filters={[
                            {
                                filterName: 'type',
                                filterIcon: <WidgetsOutlinedIcon />,
                                options: [
                                    ImportsTypeEnum.LOGISTICS,
                                    ImportsTypeEnum.FINTECH,
                                    ImportsTypeEnum.RFQ,
                                ],
                                preselected: selectedTypes,
                                setSelectedOptions: (selection) => setSelectedTypes(selection),
                                transformLabel: (label) => {
                                    return ImportsTypeLabel[label]
                                },
                            },
                        ]}
                    />
                    {paginatedImports && paginatedImports.data.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            itemsPerPage={ITEMS_PER_PAGE}
                            currentPageItemCount={paginatedImports.data.length}
                            onPageChange={handlePageChange}
                            defaultHasNext={paginatedImports.hasMore}
                            loading={loading}
                        />
                    )}
                </Box>
                <ActiveFiltersChips<string | ImportsTypeEnum>
                    filters={[
                        {
                            filterName: 'account',
                            selectedValues: selectedAccountIds,
                            setSelectedValues: (ids) => setSelectedAccountIds(ids),
                            transformLabel: (id) => accountNamesMap[id],
                        },
                        {
                            filterName: 'type',
                            selectedValues: selectedTypes,
                            setSelectedValues: (selection) =>
                                // seletion is guaranteed to be an ImportsTypeEnum
                                setSelectedTypes(selection as ImportsTypeEnum[]),
                            transformLabel: (label) => {
                                return ImportsTypeLabel[label as ImportsTypeEnum]
                            },
                        },
                    ]}
                />
                <ImportsTable
                    paginatedImports={paginatedImports}
                    currentPage={currentPage}
                    pageHistoryRef={pageHistoryRef}
                    queryParams={queryParams}
                    loading={loading}
                    accountNamesMap={accountNamesMap}
                />
            </WhiteBoxContainer>
        </MainLayoutContainerNew>
    )
}

export default Imports
